/**
 * QC charts: the plain data side — palette, vocabularies, the flat
 * per-ball record, field geometry and the innings grouping hook.
 */
import { useContext, createContext, useMemo } from "react";

// ── Theme tokens ─────────────────────────────────────────────────────
export const CHART_CSS = `
.qc-charts {
  --qc-s1: #3987e5; --qc-s2: #d95926;
  --qc-heat: 57, 135, 229;
  --qc-grid: rgba(168, 174, 189, 0.16);
  --qc-axis: rgba(168, 174, 189, 0.45);
  --qc-phase: rgba(168, 174, 189, 0.07);
}
html[data-theme="light"] .qc-charts {
  --qc-s1: #2a78d6; --qc-s2: #eb6834;
  --qc-heat: 42, 120, 214;
  --qc-grid: rgba(86, 93, 107, 0.14);
  --qc-axis: rgba(86, 93, 107, 0.45);
  --qc-phase: rgba(86, 93, 107, 0.06);
}
`;

// Run outcomes — the analyzer's palette (WAGON_OUTCOME_COLOR).
export const OC = { dot: "#64748b", r13: "#38bdf8", r23: "#0284c7", r4: "#facc15", r6: "#f97316", extra: "#a78bfa" };
export const MARK_EDGE = "rgba(15,23,42,0.6)";
// Dark halo so a label stays legible over the shot lines and the field.
export const FIELD_HALO = { paintOrder: "stroke", stroke: "rgba(8,24,14,0.9)", strokeWidth: 0.7, strokeLinejoin: "round" };

export const outcomeColour = (b) => {
  if (b.offBat >= 6) return OC.r6;
  if (b.offBat === 4 || b.offBat === 5) return OC.r4;
  if (b.offBat >= 1) return OC.r13;
  return OC.dot;
};
export const outcomeKind = (b) => {
  if (b.wicket) return "w";
  if (b.offBat >= 6) return "6";
  if (b.offBat === 4 || b.offBat === 5) return "4";
  if (b.offBat >= 1) return "13";
  return "0";
};
export const OUTCOME_LEGEND = [
  { key: "0", label: "Dot", color: OC.dot },
  { key: "13", label: "1–3", color: OC.r13 },
  { key: "4", label: "Four", color: OC.r4 },
  { key: "6", label: "Six", color: OC.r6 },
  { key: "w", label: "Wicket", shape: "x" },
];

export const NOT_DISMISSALS = new Set(["RETIRED_HURT", "RETIRED_NOT_OUT"]);

export const LINES = [
  { key: "WOO", label: "Wide off", short: "Wide" },
  { key: "OO", label: "Outside off", short: "O/off" },
  { key: "OS", label: "Off stump", short: "Off" },
  { key: "MS", label: "Middle", short: "Mid" },
  { key: "LS", label: "Leg stump", short: "Leg" },
  { key: "DL", label: "Down leg", short: "D/leg" },
  { key: "WDL", label: "Wide leg", short: "Wide" },
];
export const LINE_IDX = Object.fromEntries(LINES.map((l, i) => [l.key, i]));
export const LINE_LABEL = Object.fromEntries(LINES.map((l) => [l.key, l.label]));

// Lengths from the stumps outward. Metres are only for drawing the
// bands in proportion — the coder picks a band, not a distance.
export const LENGTH_BANDS = [
  { key: "Yorker", label: "Yorker", from: 0, to: 1.8, fill: "#eab308" },
  { key: "Full", label: "Full", from: 1.8, to: 4.5, fill: "#22c55e" },
  { key: "Length ball", label: "Good length", from: 4.5, to: 7, fill: "#ef4444" },
  { key: "Back of a length", label: "Back of a length", from: 7, to: 8.7, fill: "#a855f7" },
  { key: "Short", label: "Short", from: 8.7, to: 10.3, fill: "#3b82f6" },
  { key: "Half tracker", label: "Half tracker", from: 10.3, to: 12, fill: "#06b6d4" },
];
export const BAND_BY_KEY = Object.fromEntries(LENGTH_BANDS.map((b) => [b.key, b]));
export const NOT_PITCHED = new Set(["Full Toss", "Beamer"]);
export const GRID_LENGTHS = ["Half tracker", "Short", "Back of a length", "Length ball", "Full", "Yorker", "Full Toss", "Beamer"];
export const HEIGHTS = ["Head", "Shoulder", "Chest", "Waist", "Thigh", "Knee", "Shin", "Yorker"];

// ── One flat record per ball ─────────────────────────────────────────
export function toBall(e, i) {
  const sc = e.scoring || {};
  const rob = sc.runs_off_bat || 0;
  const extras = (sc.wide_runs || 0) + (sc.no_ball_runs || 0)
    + (sc.bye_runs || 0) + (sc.leg_bye_runs || 0) + (sc.penalty_runs || 0);
  const short = sc.is_short_run && rob > 0 ? 1 : 0;
  const offBat = rob - short;
  const wt = e.wicket?.wicket_type || null;
  const isWide = (sc.wide_runs || 0) > 0;
  const isNoBall = (sc.no_ball_runs || 0) > 0;
  const p = e.participants || {};
  const team = p.batting_team || {};
  return {
    i,
    innings: e.innings?.innings_number ?? null,
    teamId: team.id ?? null,
    teamName: team.short_name || team.name || "—",
    over: (e.sequence?.display_over_number ?? 0) + 1, // display is 0-based ("0.3")
    legal: !!e.sequence?.is_legal_delivery,
    faced: !isWide,
    isWide, isNoBall,
    offBat,
    extras,
    total: offBat + extras,
    wicket: wt && !NOT_DISMISSALS.has(wt) ? wt : null,
    lefty: String(p.striker_hand || "").toUpperCase().startsWith("L"),
    wx: e.spatial?.wagon_wheel_x ?? null,
    wy: e.spatial?.wagon_wheel_y ?? null,
    length: e.spatial?.pitch_zone || null,
    line: e.spatial?.stump_zone || null,
    height: e.spatial?.height_zone || null,
    lineAtBat: e.spatial?.batter_stump_zone || null,
    stroke: e.analytics?.stroke || null,
    label: e.sequence?.display || "",
    striker: p.striker?.short_name || p.striker?.name || "",
    bowler: p.bowler?.short_name || p.bowler?.name || "",

    // ── Identity and order, for the per-player and sequence charts ──
    id: e.id,
    entry: e,
    inningsId: e.innings?.id ?? null,
    ballNumber: e.sequence?.ball_number ?? 0,
    strikerId: p.striker?.id || null,
    nonStrikerId: p.non_striker?.id || null,
    nonStriker: p.non_striker?.short_name || p.non_striker?.name || "",
    bowlerId: p.bowler?.id || null,
    bowlerLefty: String(p.bowler_hand || "").toUpperCase().startsWith("L"),
    bowlingTeamName: p.bowling_team?.short_name || p.bowling_team?.name || "—",
    wideRuns: sc.wide_runs || 0,
    noBallRuns: sc.no_ball_runs || 0,
    byes: (sc.bye_runs || 0) + (sc.leg_bye_runs || 0),
    isBoundaryFlag: !!sc.is_boundary,
    noBallType: sc.no_ball_type || "",
    editLater: !!sc.edit_later,
    wicketRaw: wt,
    dismissedId: e.wicket?.dismissed_player?.id || null,
    dismissed: e.wicket?.dismissed_player?.name || "",
    caughtBy: e.wicket?.caught_by?.name || "",
    stumpedBy: e.wicket?.stumped_by?.name || "",
    // "Jadeja/Dhoni" when two fielders were involved — scorecard style.
    runOutBy: [e.wicket?.run_out_fielder_1?.name, e.wicket?.run_out_fielder_2?.name]
      .filter(Boolean).join("/"),

    // ── Performance data ──
    delivery: e.analytics?.delivery_type || null,
    foot: e.analytics?.foot_movement || null,
    air: e.analytics?.air_movement || null,
    control: e.analytics?.control || null,
    conn: e.analytics?.shot_connection || null,
    releasePos: e.release?.bowler_release_position || null,
    releasePoint: e.release?.bowler_release_point || null,
    structuredPos: e.spatial?.structured_position || null,
    fieldings: (e.fieldings || []).map((f) => ({
      player: f.player?.name || "",
      playerId: f.player?.id || null,
      action: f.action || "",
      position: f.fielding_position || "",
      saved: f.runs_saved || 0,
      misfielded: f.runs_misfielded || 0,
      overthrow: f.overthrow_runs || 0,
      difficulty: f.difficulty || "",
    })),
    drs: e.drs || null,
  };
}

export const ballText = (b) => {
  const what = b.wicket
    ? `WICKET (${b.wicket.replace(/_/g, " ").toLowerCase()})`
    : b.isWide ? `wide${b.total > 1 ? ` +${b.total - 1}` : ""}`
      : `${b.offBat} run${b.offBat === 1 ? "" : "s"}`;
  return [`${b.label} · ${b.bowler || "?"} to ${b.striker || "?"}`, `${what}${b.stroke ? ` · ${b.stroke}` : ""}`];
};

// Deterministic spread, so a redraw never shuffles the dots.
export function jitter(seed, mag) {
  const r = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return (r - Math.floor(r) - 0.5) * 2 * mag;
}
export const unit = (seed) => jitter(seed, 0.5) + 0.5;

export const pct = (a, b) => (b ? `${((a * 100) / b).toFixed(1)}%` : "—");
export const pct0 = (a, b) => (b ? `${Math.round((a * 100) / b)}%` : "—");

// The match's format — { code, max_overs, balls_per_over, powerplay_overs }
// from the ball-entries API — provided by QcCharts. Without it the charts
// could only guess the innings length from the balls, and drew every
// innings of 20 overs or fewer (a T10 included) as a T20.
export const QcFormatContext = createContext(null);

export function formatOvers(maxOver, fmt) {
  if (Number(fmt?.max_overs) > 0) return Number(fmt.max_overs);
  if (maxOver <= 20) return 20;
  if (maxOver <= 50) return 50;
  return maxOver;
}
export function phasesFor(overs, fmt) {
  if (Number(fmt?.max_overs) > 0) {
    const n = overs;
    const pp = Number(fmt.powerplay_overs)
      || (n >= 50 ? 10 : n >= 20 ? 6 : Math.max(1, Math.round(n * 0.3)));
    // Death = last 10 of an ODI, last 5 of a T20-length innings; a
    // short format (T10) has a powerplay and nothing else marked.
    const death = n >= 50 ? 10 : n >= 15 ? 5 : 0;
    const out = [{ from: 1, to: Math.min(pp, n), label: "Powerplay" }];
    if (death) out.push({ from: n - death + 1, to: n, label: "Death" });
    return out;
  }
  if (overs === 20) return [{ from: 1, to: 6, label: "Powerplay" }, { from: 16, to: 20, label: "Death" }];
  if (overs === 50) return [{ from: 1, to: 10, label: "Powerplay" }, { from: 41, to: 50, label: "Death" }];
  return [];
}


// ── 1. Wagon wheel (the analyzer's field) ────────────────────────────
export const FC = 50;               // field centre
export const STRIKER_Y = 43;
export const NON_STRIKER_Y = 57;
export const BOUNDARY_R = 48;
export const RING_R = 24;

// Right-hander's view, clockwise from the top-left wedge (behind the
// keeper on the off side). Left-handers' shots are mirrored into it.
export const SECTORS = [
  { start: -45, name: "Third Man", short: "Third Man", inner: "Short Third" },
  { start: 0, name: "Deep Fine Leg", short: "Fine Leg", inner: "Short Fine Leg" },
  { start: 45, name: "Deep Backward Square Leg", short: "Deep BW Sq Leg", inner: "Square Leg" },
  { start: 90, name: "Deep Mid Wicket", short: "Deep Mid Wkt", inner: "Mid Wicket" },
  { start: 135, name: "Long On", short: "Long On", inner: "Mid On" },
  { start: 180, name: "Long Off", short: "Long Off", inner: "Mid Off" },
  { start: 225, name: "Deep Cover", short: "Deep Cover", inner: "Cover" },
  { start: 270, name: "Deep Backward Point", short: "Deep BW Point", inner: "Point" },
];

export function polar(deg, r) {
  const t = ((deg - 90) * Math.PI) / 180; // 0° = up, clockwise
  return [FC + r * Math.cos(t), FC + r * Math.sin(t)];
}
export function annulus(a0, a1, r0, r1) {
  const [x1, y1] = polar(a0, r1), [x2, y2] = polar(a1, r1), [x3, y3] = polar(a1, r0), [x4, y4] = polar(a0, r0);
  return `M${x1},${y1} A${r1},${r1} 0 0 1 ${x2},${y2} L${x3},${y3} A${r0},${r0} 0 0 0 ${x4},${y4} Z`;
}
export function slice(a0, a1, r) {
  const [x1, y1] = polar(a0, r), [x2, y2] = polar(a1, r);
  return `M${FC},${FC} L${x1},${y1} A${r},${r} 0 0 1 ${x2},${y2} Z`;
}
export function sectorOf(x, y) {
  const a = (Math.atan2(x - FC, -(y - FC)) * 180) / Math.PI;
  return Math.floor(((((a + 45) % 360) + 360) % 360) / 45) % 8;
}

// Stored pins are 0..100 about the canvas centre (0..1 on some older
// rows). Project each into the field from its centre: boundaries land on
// the rope, a six just past it, everything else keeps its distance.
export function placeShots(balls) {
  const pinned = balls.filter((b) => b.wx != null && b.wy != null);
  const unitFrame = pinned.length && pinned.every((b) => Math.abs(b.wx) <= 1.5 && Math.abs(b.wy) <= 1.5);
  const c = unitFrame ? 0.5 : 50, half = unitFrame ? 0.5 : 50;
  return pinned.map((b) => {
    const rawX = b.lefty ? 2 * c - b.wx : b.wx;
    const dx = (rawX - c) / half, dy = (b.wy - c) / half;
    const r = Math.hypot(dx, dy);
    if (r <= 0) return { b, x: FC, y: FC, sector: 0 };
    const boundary = b.offBat === 4 || b.offBat === 6;
    const rr = boundary ? 1 : Math.min(r, 1);
    const R = b.offBat === 6 ? 50 : BOUNDARY_R;
    const x = FC + (dx / r) * rr * R, y = FC + (dy / r) * rr * R;
    return { b, x, y, sector: sectorOf(x, y) };
  });
}

// ── Innings grouping (Manhattan + worm) ──────────────────────────────
export function useInnings(balls) {
  const bpo = useBpo();
  return useMemo(() => {
    const byInn = new Map();
    for (const b of balls) {
      if (b.innings == null) continue;
      const inn = byInn.get(b.innings) || { innings: b.innings, teamId: b.teamId, team: b.teamName, overs: new Map(), legal: 0 };
      const o = inn.overs.get(b.over) || { over: b.over, runs: 0, wickets: 0, fours: 0, sixes: 0, extras: 0 };
      o.runs += b.total;
      o.extras += b.extras;
      if (b.offBat === 4) o.fours += 1;
      if (b.offBat === 6) o.sixes += 1;
      if (b.wicket) o.wickets += 1;
      if (b.legal) inn.legal += 1;
      inn.overs.set(b.over, o);
      byInn.set(b.innings, inn);
    }
    const list = [...byInn.values()].sort((a, b) => a.innings - b.innings);
    // Colour follows the batting TEAM (two entities), never the innings
    // index, so a Test's 3rd innings keeps its side's colour.
    const teamOrder = [];
    list.forEach((i) => { if (!teamOrder.includes(i.teamId)) teamOrder.push(i.teamId); });
    const colors = ["var(--qc-s1)", "var(--qc-s2)"];
    list.forEach((i) => {
      i.color = colors[teamOrder.indexOf(i.teamId)] || "var(--qc-s1)";
      i.rows = [...i.overs.values()].sort((a, b) => a.over - b.over);
      i.runs = i.rows.reduce((t, r) => t + r.runs, 0);
      i.wkts = i.rows.reduce((t, r) => t + r.wickets, 0);
      i.rr = i.legal ? (i.runs * bpo) / i.legal : 0;
      i.oversText = oversText(i.legal, bpo);
    });
    return list;
  }, [balls, bpo]);
}


// ── Analysis helpers shared by the Match / Batting / Bowling / Fielding /
// Coding-check tabs ──────────────────────────────────────────────────

// Entries arrive newest-first; sequence charts need innings, then ball order.
export function inOrder(balls) {
  return [...balls].sort((a, b) => (a.innings ?? 0) - (b.innings ?? 0) || a.ballNumber - b.ballNumber);
}

// Balls per over of the match's format (5 on the Hundred), 6 if unknown.
export const bpoOf = (fmt) => (Number(fmt?.balls_per_over) > 0 ? Number(fmt.balls_per_over) : 6);
export const useBpo = () => bpoOf(useContext(QcFormatContext));
export const oversText = (legal, bpo = 6) => `${Math.floor(legal / bpo)}.${legal % bpo}`;
export const sr = (runs, balls) => (balls ? ((runs * 100) / balls).toFixed(1) : "—");
export const econ = (runs, legal, bpo = 6) => (legal ? ((runs * bpo) / legal).toFixed(2) : "—");

// Runs a bowler is charged with: off the bat plus wides and no-balls.
export const conceded = (b) => b.offBat + b.wideRuns + b.noBallRuns;

// Dismissals credited to the bowler (a run-out is not his).
export const BOWLER_WICKETS = new Set([
  "BOWLED", "CAUGHT", "CAUGHT_BEHIND", "CAUGHT_AND_BOWLED", "LBW", "STUMPED", "HIT_WICKET",
]);
export const bowlerWicket = (b) => !!b.wicket && BOWLER_WICKETS.has(b.wicket);
export const niceWicket = (w) => (w ? w.replace(/_/g, " ").toLowerCase() : "");

// The coding app's delivery-type picker, grouped the same way.
export const PACE_TYPES = [
  "No Movement", "In Swinger", "Out Swinger", "Seam In", "Seam Away",
  "Reverse Inswinger", "Reverse Outswinger", "Angled In", "Angled Away",
  "Slower Ball", "Knuckle Ball", "Off Cutter", "Leg Cutter", "Back of Hand",
];
export const SPIN_TYPES = [
  "No Turn", "Off Break", "Left arm Orthodox", "Arm Ball", "Carrom Ball",
  "Doosra", "Slider", "Leg Spinner", "Googly", "Drifter", "Top Spinner",
  "Flipper", "Quicker Ball",
];
// How the ball moved (pace) — the swing & seam chart.
export const MOVEMENT_TYPES = [
  "No Movement", "In Swinger", "Out Swinger", "Seam In", "Seam Away",
  "Reverse Inswinger", "Reverse Outswinger", "Angled In", "Angled Away",
];
// Variations — pace changes of pace and every spin delivery.
export const VARIATION_TYPES = [
  "Slower Ball", "Knuckle Ball", "Off Cutter", "Leg Cutter", "Back of Hand", ...SPIN_TYPES,
];
const PACE_SET = new Set(PACE_TYPES);
const SPIN_SET = new Set(SPIN_TYPES);

// Pace or spin per ball: its own coded delivery type, else what that
// bowler's other coded balls say, else unknown.
export function styleResolver(balls) {
  const votes = new Map();
  for (const b of balls) {
    if (!b.bowlerId || !b.delivery) continue;
    const v = votes.get(b.bowlerId) || { pace: 0, spin: 0 };
    if (PACE_SET.has(b.delivery)) v.pace += 1;
    else if (SPIN_SET.has(b.delivery)) v.spin += 1;
    votes.set(b.bowlerId, v);
  }
  return (b) => {
    if (PACE_SET.has(b.delivery)) return "pace";
    if (SPIN_SET.has(b.delivery)) return "spin";
    const v = votes.get(b.bowlerId);
    if (!v || (!v.pace && !v.spin)) return "unknown";
    return v.spin > v.pace ? "spin" : "pace";
  };
}

// Shot connection, as the coding app's picker spells it.
export const CONN_GROUPS = [
  { key: "good", label: "Middled", items: ["Well Timed", "Middled"] },
  { key: "normal", label: "Normal contact", items: ["Normal"] },
  { key: "false", label: "False shot", items: [
    "Mis-timed", "Play and Miss", "Play and Miss (Legside)", "Outside Edge", "Inside Edge",
    "Top Edge", "Bottom Edge", "Leading Edge", "Thick Edge", "Spliced", "Bat-pad", "Gloved",
  ] },
  { key: "body", label: "Hit body / pad", items: ["Padded", "Hit Pad", "Hit Body", "Hit Helmet"] },
  { key: "left", label: "Left alone", items: ["Shoulder Arms", "Left"] },
];
const CONN_KEY = Object.fromEntries(CONN_GROUPS.flatMap((g) => g.items.map((i) => [i.toLowerCase(), g.key])));
export const connGroup = (b) => (b.conn ? CONN_KEY[b.conn.toLowerCase()] || "normal" : null);
export const NO_CONTACT_CONNS = new Set([
  "play and miss", "play and miss (legside)", "shoulder arms", "left", "padded", "hit pad", "hit body", "hit helmet",
]);
export const NO_CONTACT_STROKES = new Set(["no shot", "leave"]);
export const noBatContact = (b) =>
  (b.conn && NO_CONTACT_CONNS.has(b.conn.toLowerCase())) || (b.stroke && NO_CONTACT_STROKES.has(b.stroke.toLowerCase()));

// Aerial shots (air movement other than along the ground) and their control.
export const isAerial = (b) => ["flat", "loopy", "high"].includes(String(b.air || "").toLowerCase());
export const isControlled = (b) => /^air controlled$/i.test(b.control || "");
export const isUncontrolled = (b) => /^un ?cont?rolled$/i.test(b.control || "");

export const FOOT_ORDER = [
  "Front Foot", "Back Foot", "Down the Track", "Backs Away", "Deep in Crease",
  "Moves Infront", "Moves Offside", "Moves Legside", "No Movement", "Ducked", "Swayed Away",
];

export const RELEASE_POSITIONS = [
  { key: "OVER_THE_WICKET", label: "Over the wicket" },
  { key: "ROUND_THE_WICKET", label: "Round the wicket" },
  { key: "WIDE_OVER", label: "Wide, over" },
  { key: "WIDE_ROUND", label: "Wide, round" },
  { key: "DIAGONAL", label: "Diagonal" },
];
export const RELEASE_POINTS = [
  { key: "NORMAL", label: "Normal" },
  { key: "WIDE_OF_CREASE", label: "Wide of crease" },
  { key: "BEHIND_CREASE", label: "Behind crease" },
  { key: "LOW_ARM", label: "Low arm" },
];

// Where a named fielding position sits on the field, right-hander's view
// (the analyzer's POSITION_CANONICAL). Used when a catch has no wagon pin.
const P = (deg, r) => polar(deg, r);
export const POSITION_XY = {
  "Third Man": P(322.5, 42), "Deep Fine Leg": P(22.5, 42), "Deep Backward Square Leg": P(67.5, 42),
  "Deep Mid Wicket": P(112.5, 42), "Long On": P(157.5, 42), "Long Off": P(202.5, 42),
  "Deep Cover": P(247.5, 42), "Deep Backward Point": P(292.5, 42), "Long Leg": P(30, 42),
  "Long Stop": P(0, 42), "Cow Corner": P(140, 42), "Deep Extra Cover": P(220, 42),
  "Deep Square Leg": P(90, 42), "Deep Point": P(270, 42), "Straight": P(180, 42),
  "Short Third": P(322.5, 20), "Short Fine Leg": P(22.5, 20), "Square Leg": P(67.5, 20),
  "Mid Wicket": P(112.5, 20), "Mid On": P(157.5, 20), "Mid Off": P(202.5, 20), "Cover": P(247.5, 20),
  "Point": P(292.5, 20), "Cover Point": P(260, 20), "Extra Cover": P(230, 20), "Backward Point": P(280, 20),
  "Backward Square Leg": P(80, 20), "Slips": P(340, 12), "Leg Slip": P(20, 12), "Gully": P(310, 13),
  "Leg Gully": P(50, 13), "Silly Point": P(285, 11), "Silly Mid Off": P(210, 11), "Silly Mid On": P(150, 11),
  "Short Cover": P(240, 14), "Short Extra Cover": P(220, 14), "Short Mid Wicket": P(120, 14),
  "Short Leg": P(95, 12), "Short Backward Square Leg": P(78, 12), "Wicket Keeper": P(0, 10), "Bowler": P(180, 10),
};
export const positionXY = (name) => {
  if (!name) return null;
  const hit = Object.keys(POSITION_XY).find((k) => k.toLowerCase() === name.toLowerCase().replace("sqaure", "square"));
  return hit ? POSITION_XY[hit] : null;
};

// Phase of an over for the format.
export function phaseBlocks(overs, fmt) {
  const ph = phasesFor(overs, fmt);
  if (ph.length === 1) {
    return [
      { key: "pp", label: "Powerplay", from: ph[0].from, to: ph[0].to },
      { key: "mid", label: "Remaining", from: ph[0].to + 1, to: overs },
    ];
  }
  if (ph.length) {
    return [
      { key: "pp", label: "Powerplay", from: ph[0].from, to: ph[0].to },
      { key: "mid", label: "Middle", from: ph[0].to + 1, to: ph[1].from - 1 },
      { key: "death", label: "Death", from: ph[1].from, to: ph[1].to },
    ];
  }
  const out = [];
  for (let f = 1; f <= overs; f += 10) out.push({ key: `o${f}`, label: `Overs ${f}–${Math.min(f + 9, overs)}`, from: f, to: Math.min(f + 9, overs) });
  return out;
}
